import { useEffect } from 'react'
import {
  BooleanFlagsSchema,
  defaultInputRemoteConfig,
  JSONFlagsSchema,
  NumberFlagsSchema,
  RemoteConfigSchema,
  StringFlagsSchema
} from './remoteConfigSchema'
import { useRemoteConfig, useRemoteConfigStatus, useRemoteConfigUserContext } from './useRemoteConfig'
import {
  fetchAndActivate,
  FirebaseRemoteConfigTypes,
  getRemoteConfig,
  onConfigUpdated
} from '@react-native-firebase/remote-config'
import z from 'zod'
import { rootStore } from '../../app/models'
import isEqual from 'lodash/isEqual'
import { Platform } from 'react-native'
import { firebaseApp } from '../firebase'
import { getApp, getApps, initializeApp } from '@react-native-firebase/app'

function getFormatedFlag(key: string, value: FirebaseRemoteConfigTypes.ConfigValue) {
  if (key in JSONFlagsSchema.shape) {
    const parsedValue = JSON.parse(value.asString())
    return parsedValue
  }
  if (key in NumberFlagsSchema.shape) {
    return value.asNumber()
  }
  if (key in BooleanFlagsSchema.shape) {
    return value.asBoolean()
  }
  if (key in StringFlagsSchema.shape) {
    return value.asString()
  }
}

let initialFlagEvent = true
function parseFlags() {
  const remoteConfig = getRemoteConfig(firebaseApp())
  const allFlags = remoteConfig.getAll()
  const input = Object.entries(allFlags).reduce(
    (acc, [key, value]) => {
      const formatedValue = getFormatedFlag(key, value)
      // @ts-expect-error ts can't infer acc[key] is scoped to the correct type
      acc[key] = formatedValue
      return acc
    },
    {} as z.infer<typeof RemoteConfigSchema>
  )
  const parsed = RemoteConfigSchema.parse(input)
  return parsed
}

export const initRemoteConfig = async () => {
  if (Platform.OS !== 'web' && !getApps().find((app) => app.name === '[DEFAULT]')) {
    await initializeApp({
      apiKey: 'AIzaSyDuEU6qQp-R6lHNXa00qoaZkuFWk5YCIGI',
      authDomain: 'poker-powher.firebaseapp.com',
      databaseURL: 'https://poker-powher-default-rtdb.firebaseio.com',
      projectId: 'poker-powher',
      storageBucket: 'poker-powher.firebasestorage.app',
      messagingSenderId: '469309110195',
      appId: '1:469309110195:web:950bb015b0946fa3e07dc6',
      measurementId: 'G-CX8TLGZ6EH'
    })
  }
  const remoteConfig = getRemoteConfig(firebaseApp())

  try {
    // await remoteConfig.ensureInitialized()
    await remoteConfig.setConfigSettings({
      fetchTimeMillis: 60000,
      // Once we know the experiment id, we can set a larger fetch interval
      minimumFetchIntervalMillis: __DEV__ || !useRemoteConfig.getState().experiment_id ? 1000 : 3600
    })

    await remoteConfig.setDefaults(defaultInputRemoteConfig)
    const userContext = Platform.OS === 'web' ? useRemoteConfigUserContext.getState() : undefined
    await fetchAndActivate(remoteConfig, userContext)
    const initialFlags = parseFlags()
    if (!isEqual(initialFlags, useRemoteConfig.getState()) || initialFlagEvent) {
      initialFlagEvent = false
      useRemoteConfig.setState(initialFlags)
      rootStore?.analytics?.setEvent('Feature Flags', useRemoteConfig.getState())
    }
  } catch (e) {
    console.error('Error initializing remote config', e)
  } finally {
    useRemoteConfigStatus.setState({ isInitialized: true })
  }
}

function onRealTimeActivate(updatedKeys?: string[]) {
  const remoteConfig = getRemoteConfig(firebaseApp())

  // The updated keys are the keys that were changed
  const flagsToUpdate = updatedKeys?.reduce((acc, _key) => {
    const key = _key as keyof (typeof RemoteConfigSchema)['shape']
    const value = remoteConfig.getValue(key)
    const formatedValue = getFormatedFlag(key, value)
    const validation = RemoteConfigSchema.shape[key].safeParse(formatedValue)
    if (!validation.success) {
      console.error(`Remote config ${key} parsedInput.error`, validation.error)
    } else if (!isEqual(validation.data, useRemoteConfig.getState()[key])) {
      // @ts-expect-error ts can't infer acc[key] is scoped to the correct type
      acc[key] = validation.data
    }
    return acc
  }, {})

  if (flagsToUpdate) {
    useRemoteConfig.setState(flagsToUpdate)
    rootStore?.analytics?.setEvent('Feature Flags', useRemoteConfig.getState())
  }
}

export const useInitRemoteConfig = () => {
  useEffect(() => {
    let remoteConfigListenerUnsubscriber: (() => void) | undefined

    initRemoteConfig()
      .then(() => {
        const remoteConfig = getRemoteConfig(firebaseApp())
        remoteConfigListenerUnsubscriber = onConfigUpdated(remoteConfig, (event, error) => {
          if (error !== undefined) {
            console.error(`remote-config listener subscription error: ${JSON.stringify(error)}`)
          } else {
            // If you use realtime updates, the SDK fetches the new config for you.
            // However, you must activate the new config so it is in effect
            remoteConfig
              .fetchAndActivate()
              .then((activated) => {
                if (activated) {
                  return remoteConfig.ensureInitialized()
                }
                console.warn('No configs were activated.')
              })
              .then(() => onRealTimeActivate(event?.updatedKeys))
          }
        })
        console.log('Remote config listener initialized')
      })
      .catch((e) => {
        console.error('Error initializing remote config listener', e)
      })
    // Web doesn't support realtime updates
    if (Platform.OS !== 'web') {
      return remoteConfigListenerUnsubscriber?.()
    }
  }, [])
}
